import { RefObject, useEffect, useRef, useState } from 'react'

export const TEXT_SIZE_MIN_PX = 8
export const TEXT_SIZE_MAX_PX = 120
export const TICKER_HEIGHT_MIN_PX = 60
export const TICKER_HEIGHT_MAX_PX = 400

export const outerLayoutHeight = (el: Element): number => {
  const cs = globalThis.getComputedStyle(el)
  const px = (v: string): number => {
    const n = Number.parseFloat(v)
    return Number.isNaN(n) ? 0 : n
  }
  const height = px(cs.height)
  if (cs.boxSizing === 'border-box') return height
  return height + px(cs.paddingTop) + px(cs.paddingBottom) + px(cs.borderTopWidth) + px(cs.borderBottomWidth)
}

export const previewOverflows = (root: Element): boolean => {
  if (!(root instanceof HTMLElement)) return false
  const scale = root.offsetWidth > 0 ? root.getBoundingClientRect().width / root.offsetWidth : 1
  const clippers = [root, ...Array.from(root.querySelectorAll('*'))].filter((node): node is HTMLElement => {
    if (!(node instanceof HTMLElement)) return false
    const cs = globalThis.getComputedStyle(node)
    return cs.overflowX === 'hidden' && cs.textOverflow !== 'ellipsis' && node.scrollWidth > node.clientWidth + 1
  })
  return clippers.some((box) => {
    const cs = globalThis.getComputedStyle(box)
    const r = box.getBoundingClientRect()
    const left = r.left + Number.parseFloat(cs.borderLeftWidth) * scale - scale
    const right = r.right - Number.parseFloat(cs.borderRightWidth) * scale + scale
    const walker = globalThis.document.createTreeWalker(box, NodeFilter.SHOW_TEXT)
    for (let t = walker.nextNode(); t != null; t = walker.nextNode()) {
      if ((t.textContent ?? '').trim() === '') continue
      let ellipsis = false
      for (let a = t.parentElement; a != null && a !== box; a = a.parentElement) {
        if (globalThis.getComputedStyle(a).textOverflow === 'ellipsis') ellipsis = true
      }
      if (ellipsis) continue
      const range = globalThis.document.createRange()
      range.selectNodeContents(t)
      if (Array.from(range.getClientRects()).some((tr) => tr.width > 0 && (tr.left < left || tr.right > right))) return true
    }
    return false
  })
}

export interface PreviewFit {
  rootHeight: number
  frameHeight: number
  overflows: boolean
}

const EMPTY_FIT: PreviewFit = { rootHeight: 0, frameHeight: 0, overflows: false }

export const usePreviewFit = (testId: string): [RefObject<HTMLDivElement | null>, PreviewFit] => {
  const ref = useRef<HTMLDivElement>(null)
  const [fit, setFit] = useState<PreviewFit>(EMPTY_FIT)
  useEffect(() => {
    const wrapper = ref.current
    if (wrapper == null) return
    let frame: number | null = null
    let observed: Element[] = []
    let resize: ResizeObserver | null = null
    const measure = (): void => {
      frame = null
      const roots = Array.from(wrapper.querySelectorAll(`[data-testid="${testId}"]`))
      if (resize != null && (roots.length !== observed.length || roots.some((r, i) => r !== observed[i]))) {
        resize.disconnect()
        for (const r of roots) resize.observe(r)
        observed = roots
      }
      const next: PreviewFit = {
        rootHeight: Math.max(0, ...roots.map(outerLayoutHeight)),
        frameHeight: Math.max(0, ...roots.map((r) => r.parentElement == null ? 0 : outerLayoutHeight(r.parentElement))),
        overflows: roots.some(previewOverflows)
      }
      setFit((prev) => prev.rootHeight === next.rootHeight &&
        prev.frameHeight === next.frameHeight &&
        prev.overflows === next.overflows
        ? prev
        : next)
    }
    const schedule = (): void => {
      if (frame == null) frame = globalThis.requestAnimationFrame(measure)
    }
    if (typeof ResizeObserver !== 'undefined') resize = new ResizeObserver(schedule)
    const mutations = new MutationObserver(schedule)
    mutations.observe(wrapper, { childList: true, subtree: true, attributes: true, characterData: true })
    const fonts = globalThis.document?.fonts
    fonts?.addEventListener('loadingdone', schedule)
    schedule()
    return () => {
      if (frame != null) globalThis.cancelAnimationFrame(frame)
      mutations.disconnect()
      resize?.disconnect()
      fonts?.removeEventListener('loadingdone', schedule)
    }
  }, [testId])
  return [ref, fit]
}
